import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  UserPlus,
  Trash2,
  Shield,
  Users as UsersIcon,
  KeyRound,
  Power,
} from "lucide-react";
import { api, apiErrorMessage } from "../lib/api";
import { dateTR } from "../lib/format";
import { useAuth } from "../lib/auth";
import { Skeleton } from "../components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";

function AddMemberDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    role: "member",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/users", form);
      toast.success(`${form.name} eklendi`);
      onCreated();
      onOpenChange(false);
      setForm({ name: "", email: "", password: "", role: "member" });
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    "w-full h-11 px-3.5 rounded-xl border border-stone-300 bg-white focus:border-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-600/20";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">Yeni Üye Ekle</DialogTitle>
          <DialogDescription>
            Verdiğiniz e-posta ve şifre ile giriş yapabilirler.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">
              Ad Soyad
            </label>
            <input
              required
              data-testid="member-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className={"mt-1.5 " + inputCls}
              placeholder="Ayşe Demir"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">
              E-posta
            </label>
            <input
              type="email"
              required
              data-testid="member-email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className={"mt-1.5 " + inputCls}
              placeholder="ornek@galeri.com"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">
              Şifre
            </label>
            <input
              type="text"
              required
              minLength={6}
              data-testid="member-password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className={"mt-1.5 " + inputCls}
              placeholder="En az 6 karakter"
            />
            <p className="text-[11px] text-stone-500 mt-1">
              Bu şifreyi çalışanınıza güvenli şekilde iletin. Sonradan bu
              ekrandan sıfırlayabilirsiniz.
            </p>
          </div>
          <div>
            <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">
              Rol
            </label>
            <select
              data-testid="member-role"
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              className={"mt-1.5 " + inputCls}
            >
              <option value="member">Üye</option>
              <option value="admin">Yönetici</option>
            </select>
          </div>
          <DialogFooter>
            <button
              type="submit"
              disabled={busy}
              data-testid="member-submit"
              className="h-11 px-6 rounded-full bg-orange-700 hover:bg-orange-800 text-white font-semibold text-sm disabled:opacity-60"
            >
              {busy ? "Ekleniyor…" : "Üye Ekle"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordDialog({ member, onClose, onDone }) {
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/users/${member.id}/reset-password`, { password: pwd });
      toast.success(`${member.name} için yeni şifre atandı`);
      onDone();
      onClose();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">Şifre Sıfırla</DialogTitle>
          <DialogDescription>
            <b>{member.name}</b> ({member.email}) için yeni bir şifre belirleyin.
            Bu şifreyi çalışanınıza güvenli şekilde iletin.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">
              Yeni Şifre
            </label>
            <input
              type="text"
              required
              minLength={6}
              autoFocus
              data-testid="reset-pwd"
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              className="mt-1.5 w-full h-11 px-3.5 rounded-xl border border-stone-300 focus:border-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-600/20"
              placeholder="En az 6 karakter"
            />
          </div>
          <DialogFooter>
            <button
              type="submit"
              disabled={busy}
              data-testid="reset-submit"
              className="h-11 px-6 rounded-full bg-orange-700 hover:bg-orange-800 text-white font-semibold text-sm disabled:opacity-60"
            >
              {busy ? "Kaydediliyor…" : "Şifreyi Kaydet"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Members() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);

  const load = async () => {
    try {
      const res = await api.get("/users");
      setList(res.data);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const remove = async (id) => {
    if (!window.confirm("Bu üyeyi çıkarmak istediğinize emin misiniz?")) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("Üye çıkarıldı");
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  const toggleStatus = async (u) => {
    const next = u.status === "approved" ? "disabled" : "approved";
    const verb = next === "disabled" ? "devre dışı bırak" : "etkinleştir";
    if (!window.confirm(`${u.name} adlı üyeyi ${verb}mak istediğinize emin misiniz?`)) return;
    try {
      await api.post(`/users/${u.id}/status`, { status: next });
      toast.success(next === "disabled" ? "Üye devre dışı bırakıldı" : "Üye etkinleştirildi");
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  return (
    <div data-testid="members-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs tracking-[0.3em] uppercase text-stone-500">
            Üye Yönetimi
          </div>
          <h1 className="font-display font-black text-3xl md:text-4xl text-slate-900 mt-1">
            {user?.company_name || "Galeri"} — Üyeler
          </h1>
          <p className="text-stone-500 text-sm mt-2 max-w-2xl">
            Çalışanlarınıza hesap açın, şifrelerini sıfırlayın, hesabı etkin
            veya devre dışı yapın.
          </p>
        </div>
        <button
          onClick={() => setAddOpen(true)}
          data-testid="member-add-btn"
          className="h-11 px-5 rounded-full bg-orange-700 hover:bg-orange-800 text-white font-semibold text-sm flex items-center gap-2"
        >
          <UserPlus className="w-4 h-4" /> Yeni Üye
        </button>
      </div>

      <div className="rounded-2xl border border-stone-200 bg-white overflow-hidden">
        <div className="px-5 py-3 border-b border-stone-200 bg-stone-50/60 flex items-center gap-2">
          <UsersIcon className="w-4 h-4 text-stone-500" />
          <span className="text-[11px] font-semibold uppercase tracking-widest text-stone-500">
            Tüm üyeler
          </span>
        </div>
        {loading ? (
          <div className="p-6 space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-14" />
            ))}
          </div>
        ) : list.length === 0 ? (
          <div className="p-14 text-center text-stone-500">Henüz üye yok.</div>
        ) : (
          <div className="divide-y divide-stone-100">
            {list.map((u) => {
              const disabled = u.status === "disabled";
              return (
                <div
                  key={u.id}
                  data-testid={`member-${u.id}`}
                  className={`px-5 py-4 flex flex-wrap items-center gap-3 ${
                    disabled ? "opacity-60" : ""
                  }`}
                >
                  <div className="w-10 h-10 rounded-full bg-slate-900 text-white grid place-items-center font-display font-black">
                    {(u.name || u.email).charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900 truncate flex items-center gap-2">
                      {u.name || u.email}
                      {u.id === user?.id && (
                        <span className="text-[10px] uppercase tracking-widest text-orange-700">
                          Siz
                        </span>
                      )}
                      {disabled && (
                        <span className="text-[10px] uppercase tracking-widest bg-stone-200 text-stone-700 rounded-full px-2 py-0.5">
                          Devre Dışı
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-stone-500 truncate">
                      {u.email} · {dateTR(u.created_at)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {u.role === "admin" ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-slate-900 text-white">
                        <Shield className="w-3 h-3" /> Yönetici
                      </span>
                    ) : (
                      <span className="inline-flex px-2.5 py-1 rounded-full text-[11px] font-semibold bg-stone-100 text-stone-700">
                        Üye
                      </span>
                    )}
                  </div>
                  {u.id !== user?.id && (
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => setResetTarget(u)}
                        data-testid={`reset-${u.id}`}
                        className="h-9 px-3 rounded-full border border-stone-300 text-stone-700 hover:bg-stone-50 text-sm font-medium flex items-center gap-1.5"
                        title="Şifre sıfırla"
                      >
                        <KeyRound className="w-4 h-4" /> Şifre
                      </button>
                      <button
                        onClick={() => toggleStatus(u)}
                        data-testid={`toggle-${u.id}`}
                        className={`h-9 px-3 rounded-full border text-sm font-medium flex items-center gap-1.5 ${
                          disabled
                            ? "border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                            : "border-amber-200 text-amber-800 hover:bg-amber-50"
                        }`}
                        title={disabled ? "Etkinleştir" : "Devre dışı bırak"}
                      >
                        <Power className="w-4 h-4" />
                        {disabled ? "Etkinleştir" : "Devre Dışı"}
                      </button>
                      <button
                        onClick={() => remove(u.id)}
                        data-testid={`remove-${u.id}`}
                        className="h-9 w-9 rounded-full border border-rose-200 text-rose-700 hover:bg-rose-50 grid place-items-center"
                        title="Üyeyi çıkar"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <AddMemberDialog open={addOpen} onOpenChange={setAddOpen} onCreated={load} />
      {resetTarget && (
        <ResetPasswordDialog
          member={resetTarget}
          onClose={() => setResetTarget(null)}
          onDone={load}
        />
      )}
    </div>
  );
}
